import _ from 'lodash'
import React from 'react'
import Steps from 'antd/es/steps'
import type { StepsProps } from 'antd/es/steps'
import 'antd/es/steps/style'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'

import 'browser/components/atomic-elements/atoms/progress-steps/_progress-steps.scss'
import classNames from 'classnames'

type StepItem = NonNullable<StepsProps['items']>[number]

type KeyedStepItem = StepItem & { key: React.Key }

interface IProgressStep extends StepItem {
  predicate: string
  hiddenPredicate?: string
  label: string
  key?: React.Key
}

/**
 * @uiComponent
 */
type IProgressStepsProps = IBaseProps & {
  frames: any
  steps: IProgressStep[]
  stepType: StepsProps['type']
  transitionWidth?: number
} & Omit<StepsProps, 'items' | 'type'>

type IProgressStepsState = {
  forceVertical: boolean
}

export class ProgressSteps extends React.PureComponent<IProgressStepsProps, IProgressStepsState> {
  private containerRef = React.createRef<HTMLDivElement>()
  private resizeObserver?: ResizeObserver

  public state: IProgressStepsState = {
    forceVertical: false,
  }

  public render() {
    const {
      frames,
      steps,
      current: currentProp,
      stepType,
      direction,
      className,
      ...rest
    } = this.props

    if (_.isEmpty(steps)) {
      return null
    }

    const currentPosition = this.getCurrentPosition()
    const safeCurrent = typeof currentProp === 'number'
      ? currentProp
      : (currentPosition >= 0 ? currentPosition + 1 : 0)

    const computedDirection = direction == 'horizontal'
      ? (this.state.forceVertical ? 'vertical' : 'horizontal')
      : direction

    const items = steps.reduce<KeyedStepItem[]>((filtered, step, idx) => {
      if (this.shouldHideStep(step)) {
        return filtered
      }
      filtered.push({
        ...step,
        title: step.label,
        key: step.label || step.key || idx,
        icon: this.renderIcon(step.icon, idx),
      })
      return filtered
    }, [])

    return (
      <div ref={this.containerRef}>
        <Steps
          {...rest}
          className={classNames('vector-progress-steps', className)}
          direction={computedDirection}
          current={safeCurrent}
          type={stepType}
          items={items}
        />
      </div>
    )
  }

  public componentDidMount() {
    this.subscribeResizeObserver()
    this.updateDirectionFromWidth()
  }

  public componentDidUpdate(prevProps: IProgressStepsProps) {
    if (prevProps.transitionWidth !== this.props.transitionWidth) {
      this.updateDirectionFromWidth()
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
  }

  // Finds the last step item where the predicate evaluates to true
  private getCurrentPosition() {
    const { frames, steps } = this.props
    return _.findLastIndex(steps, (step) => {
      return evaluateExpressionWithScopes(frames, step.predicate, {
        ...CustomFormulas,
      })
    })
  }

  // Resize observer handles direction changes when container shrinks
  // `antd` has a responsive flag but it only tracks the window size, not container size
  private subscribeResizeObserver() {
    if (!this.containerRef.current) {
      return
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.updateDirectionFromWidth()
    })
    this.resizeObserver.observe(this.containerRef.current)
  }

  // Checks the current width of the container and flips direction to vertical
  // if it's below the transitionWidth prop
  private updateDirectionFromWidth() {
    const breakpoint = this.props.transitionWidth ?? 420
    const width = this.containerRef.current?.offsetWidth
    if (!width) {
      return
    }
    const forceVertical = width < breakpoint
    if (forceVertical !== this.state.forceVertical) {
      this.setState({ forceVertical })
    }
  }

  private shouldHideStep(step: IProgressStep) {
    if (!step.hiddenPredicate) {
      return false
    }
    const { frames } = this.props
    return evaluateExpressionWithScopes(frames, step.hiddenPredicate, { ...CustomFormulas, });
  } 

  private renderIcon(iconValue: IProgressStep['icon'], index: number) {
    const { frames } = this.props
    if (_.isNil(iconValue)) {
      return undefined
    }
    if (typeof iconValue == 'string' || React.isValidElement(iconValue)) {
      return iconValue
    }
    const renderer = frames?.getContext?.('renderer')
    if (!renderer) {
      return undefined
    }
    const uiSchemaPath = frames.getContext('uiSchemaPath').concat(['steps', index, 'icon'])
    const context = {
      uiSchema: iconValue,
      uiSchemaPath,
    }
    const newFrames = renderer.createChildFrame(frames, context)
    return renderer.createElementFromFrame(newFrames)
  }
}
